import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { billYearLabel } from '../lib/billYear';
import { Badge, Button } from '../atoms';
import { PageHeader } from '../shell';
import { DataTable, Pagination, CreateEntityModal, ListToolbar } from '../organisms';
import { useServerList } from '../lib/useServerList';
import { formatDate } from '../lib/orgTimezone';

/**
 * TaxBillsList — /tax-bills
 *
 * Bespoke tax-bills list (replaces the schema-driven GenericList) so operators
 * get a real, usable STATUS filter — Yadi's explicit ask. The status control
 * offers the individual lifecycle statuses PLUS a "Pending" pseudo-status meaning
 * the active-work set (no_activity + received + processed), which is what the
 * dashboard "My Pending Bills" KPI counts and links to (`/tax-bills?status=pending`).
 *
 * Deep-link: ?status=<value> is read once at mount into the status filter, so a
 * dashboard tile (or any link) lands the list already filtered. The pseudo-status
 * `pending` expands to the comma set the generic-CRUD list turns into `IN (…)`.
 *
 * Server-side search / filter / sort / paginate via useServerList (PRD 06.1) —
 * org + SoD row-scope enforced server-side; money columns (tax_amount) stripped
 * for money-blind roles, so amounts fall back to "—".
 */

interface TaxBill {
  id: string;
  account_number: string | null;
  statement_label: string | null;
  type: string | null;
  status: string;
  tax_amount: number | null;
  due_date: string | null;
  tax_year: number | null;
  process_year: number | null; // RH§1 — the payable year (Siger "Process")
  collector_id: string | null; // §4.5 — the bill's collector (grouping key)
}

// Sum that stays null when EVERY input is null (money-blind: tax_amount stripped) —
// otherwise the sum of the present figures, so a subtotal reads "—" not "$0".
const sumOrNull = (values: Array<number | null | undefined>) =>
  values.every((v) => v == null) ? null : values.reduce((a: number, v) => a + (v ?? 0), 0);

// The active-work set the "Pending" pseudo-status expands to (matches the
// dashboard myPendingBills count). Sent as a comma list → the backend IN (…).
const PENDING_SET = 'no_activity,received,processed';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending (active work)' },
  { value: 'no_activity', label: 'No activity' },
  { value: 'received', label: 'Received' },
  { value: 'processed', label: 'Processed' },
  { value: 'approved', label: 'Approved' },
  { value: 'transmitted', label: 'Transmitted' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Void' },
];

const statusVariant: Record<string, 'success' | 'warning' | 'neutral' | 'info' | 'danger'> = {
  no_activity: 'neutral',
  received: 'warning',
  processed: 'info',
  approved: 'success',
  transmitted: 'info',
  paid: 'success',
  void: 'danger',
};

const money = (v: number | null | undefined) =>
  v != null ? `$${Math.round(v).toLocaleString()}` : '—';

export function TaxBillsList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  // Read the deep-link status once at mount; the toolbar owns it thereafter.
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') ?? '');
  // "My pending bills" deep-link: ?assigned=me scopes to the caller's own book, so
  // the personal KPI card lands on exactly the bills it counted (a firm-wide role
  // has no row scope of its own, so without this the list would open org-wide).
  const [assignedFilter] = useState(() => searchParams.get('assigned') ?? '');
  // §4.5 — group the bills under their collector with per-collector subtotals + an
  // overall total. When grouping, load a large page so the subtotals cover the whole
  // filtered set rather than one page of 25.
  const [groupByCollector, setGroupByCollector] = useState(false);

  // Map the display value to the server param: the "pending" pseudo-status expands
  // to the comma set; every real status passes through as an exact match.
  const serverStatus = statusFilter === 'pending' ? PENDING_SET : statusFilter;

  const list = useServerList<TaxBill>({
    resource: 'tax-bills',
    queryKey: ['tax-bills-list'],
    defaultPageSize: 25,
    defaultSort: { id: 'due_date', desc: false },
    filters: { status: serverStatus, assigned: assignedFilter },
  });

  const columns = useMemo<ColumnDef<TaxBill>[]>(() => [
    {
      accessorKey: 'account_number',
      header: 'Account #',
      cell: ({ getValue }) => (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)' }}>
          {(getValue() as string) || '—'}
        </span>
      ),
    },
    { accessorKey: 'statement_label', header: 'Statement', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 'tax_year', header: 'Year', cell: ({ row }) => billYearLabel(row.original.tax_year, row.original.process_year) },
    { accessorKey: 'tax_amount', header: 'Amount', cell: ({ getValue }) => money(getValue() as number | null) },
    { accessorKey: 'due_date', header: 'Due', cell: ({ getValue }) => formatDate(getValue() as string | null) },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const s = String(getValue() || 'no_activity');
        return <Badge variant={statusVariant[s] ?? 'neutral'} dot>{s.replace(/_/g, ' ')}</Badge>;
      },
    },
  ], []);

  const collectorsQuery = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['collectors-options'],
    queryFn: ({ signal }) => api.get<Array<{ id: string; name: string }>>('/api/collectors', signal),
  });
  const parcelsQuery = useQuery<Array<{ id: string; account_number: string; name: string | null }>>({
    queryKey: ['parcels-options'],
    queryFn: ({ signal }) => api.get<Array<{ id: string; account_number: string; name: string | null }>>('/api/parcels', signal),
  });
  const fkOptions = useMemo(() => ({
    collector_id: (collectorsQuery.data ?? []).map(c => ({ value: c.id, label: c.name })),
    parcel_id: (parcelsQuery.data ?? []).map(p => ({ value: p.id, label: p.name || p.account_number })),
  }), [collectorsQuery.data, parcelsQuery.data]);

  // collector_id → name, for the grouped-view headers.
  const collectorNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of collectorsQuery.data ?? []) m.set(c.id, c.name);
    return m;
  }, [collectorsQuery.data]);

  const { query, rows, total } = list;
  const hasFilters = list.search !== '' || statusFilter !== '';

  // Toggle grouping. Loading a big page when grouped makes the per-collector
  // subtotals + overall total reflect the whole filtered set, not one page.
  const toggleGroup = () => {
    const next = !groupByCollector;
    setGroupByCollector(next);
    list.setPageSize(next ? 200 : 25);
    list.setPageIndex(0);
  };

  // Rows grouped under their collector, each with a tax subtotal; sorted by collector
  // name, then an overall total across the shown bills.
  const collectorGroups = useMemo(() => {
    const byCollector = new Map<string, { collectorId: string; collectorName: string; bills: TaxBill[] }>();
    for (const b of rows) {
      const key = b.collector_id ?? '__none__';
      let g = byCollector.get(key);
      if (!g) {
        g = { collectorId: key, collectorName: b.collector_id ? (collectorNameById.get(b.collector_id) ?? 'Collector') : 'Unassigned collector', bills: [] };
        byCollector.set(key, g);
      }
      g.bills.push(b);
    }
    return [...byCollector.values()]
      .map((g) => ({ ...g, subtotal: sumOrNull(g.bills.map((b) => b.tax_amount)) }))
      .sort((a, b) => a.collectorName.localeCompare(b.collectorName));
  }, [rows, collectorNameById]);
  const overallTotal = useMemo(() => sumOrNull(rows.map((b) => b.tax_amount)), [rows]);

  return (
    <>
      <PageHeader
        title="Tax Bills"
        subtitle={query.isLoading ? 'Loading…' : `${total.toLocaleString()} ${total === 1 ? 'bill' : 'bills'}`}
        actions={
          <>
            <Button
              variant={groupByCollector ? 'primary' : 'secondary'}
              size="md"
              onClick={toggleGroup}
              aria-pressed={groupByCollector}
            >
              {groupByCollector ? 'Grouped by collector' : 'Group by collector'}
            </Button>
            <Button variant="primary" size="md" onClick={() => setCreateOpen(true)}>
              + New tax bill
            </Button>
          </>
        }
      />

      <CreateEntityModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        table="tax_bills"
        routePath="tax-bills"
        fkOptions={fkOptions}
        invalidateKeys={[['tax-bills-list']]}
      />

      <ListToolbar
        searchValue={list.searchInput}
        onSearchChange={list.setSearchInput}
        searchPlaceholder="Search tax bills by account # or status…"
        filters={[
          { key: 'status', value: statusFilter, onChange: setStatusFilter, placeholder: 'All statuses', options: STATUS_OPTIONS, preserveOrder: true, widthPx: 200 },
        ]}
      />

      {query.error && (
        <div style={{
          padding: 'var(--sp-4)',
          background: 'var(--color-status-red-bg)',
          border: '1px solid var(--color-status-red)',
          color: 'var(--color-status-red)',
          borderRadius: 'var(--r-lg)',
          marginBottom: 'var(--sp-4)',
        }}>
          <strong>Failed to load tax bills:</strong> {(query.error as Error).message}
        </div>
      )}

      {!query.isLoading && total === 0 && !query.error && (
        <div style={{
          padding: 'var(--sp-7)',
          background: 'var(--color-card)',
          border: '1px dashed var(--color-border)',
          borderRadius: 'var(--r-lg)',
          color: 'var(--color-muted)',
          textAlign: 'center',
        }}>
          <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>
            {hasFilters ? 'No tax bills match this status.' : 'No tax bills yet.'}
          </p>
        </div>
      )}

      {(total > 0 || query.isFetching) && !query.error && !groupByCollector && (
        <>
          <DataTable
            data={rows}
            columns={columns}
            sorting={list.sorting}
            onSortingChange={list.setSorting}
            onRowClick={(row) => navigate(`/tax-bills/${row.id}`)}
            emptyMessage={query.isFetching ? 'Loading…' : 'No tax bills'}
          />
          <Pagination
            pageIndex={list.pageIndex}
            pageCount={list.pageCount}
            pageSize={list.pageSize}
            totalRows={total}
            onPageChange={list.setPageIndex}
            onPageSizeChange={list.setPageSize}
          />
        </>
      )}

      {/* §4.5 — bills grouped under their collector, each with a subtotal, plus an
          overall total across the shown bills. */}
      {(total > 0 || query.isFetching) && !query.error && groupByCollector && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          {collectorGroups.map((g) => (
            <div key={g.collectorId} style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', overflow: 'hidden' }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-3)',
                padding: 'var(--sp-3) var(--sp-4)', background: 'var(--color-card)', borderBottom: '1px solid var(--color-border)',
              }}>
                <span style={{ fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>{g.collectorName}</span>
                <span style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
                  {g.bills.length} {g.bills.length === 1 ? 'bill' : 'bills'} · <strong style={{ color: 'var(--color-ink)' }}>{money(g.subtotal)}</strong>
                </span>
              </div>
              {g.bills.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => navigate(`/tax-bills/${b.id}`)}
                  style={{
                    width: '100%', display: 'grid', gridTemplateColumns: '1.4fr 1.4fr 0.8fr 1fr 1fr auto', gap: 'var(--sp-2)',
                    alignItems: 'center', padding: 'var(--sp-2) var(--sp-4)', background: 'none', border: 'none',
                    borderBottom: '1px solid var(--color-border)', cursor: 'pointer', font: 'inherit', textAlign: 'left', color: 'var(--color-ink)',
                  }}
                >
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)' }}>{b.account_number || '—'}</span>
                  <span>{b.statement_label || '—'}</span>
                  <span>{billYearLabel(b.tax_year, b.process_year)}</span>
                  <span>{formatDate(b.due_date)}</span>
                  <span style={{ textAlign: 'right', fontWeight: 'var(--fw-medium)' }}>{money(b.tax_amount)}</span>
                  <Badge variant={statusVariant[b.status] ?? 'neutral'} dot>{String(b.status || 'no_activity').replace(/_/g, ' ')}</Badge>
                </button>
              ))}
            </div>
          ))}
          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)', alignItems: 'baseline',
            padding: 'var(--sp-3) var(--sp-4)', borderTop: '2px solid var(--color-border)',
          }}>
            <span style={{ color: 'var(--color-ink-3)' }}>Total ({rows.length} shown)</span>
            <strong style={{ color: 'var(--color-ink)', fontSize: 'var(--fs-lg)' }}>{money(overallTotal)}</strong>
          </div>
        </div>
      )}
    </>
  );
}
